import { memo } from 'react';
import { Blend, Image as ImageIcon, UserX } from 'lucide-react';
import { Button, cn } from '@cyphex/ui';
import type {
  BlurStrength, VideoEffectKind, VideoEffectPreferences, VideoEffectRuntime,
} from '../../media/settings/preferences';
import { BackgroundGallery } from './BackgroundGallery';
import { SettingRow } from './SettingRow';

export interface EffectsTabProps {
  video: VideoEffectPreferences;
  runtime: VideoEffectRuntime;
  onChange: (patch: Partial<VideoEffectPreferences>) => void;
}

const KINDS: readonly { id: VideoEffectKind; label: string; icon: typeof Blend }[] = [
  { id: 'none', label: 'None', icon: UserX },
  { id: 'blur', label: 'Blur', icon: Blend },
  { id: 'image', label: 'Image', icon: ImageIcon },
];

const STRENGTHS: readonly { id: BlurStrength; label: string }[] = [
  { id: 'light', label: 'Light' },
  { id: 'strong', label: 'Strong' },
];

/**
 * Background blur and virtual backgrounds. Real person segmentation runs in
 * this browser and the composited canvas is what gets published — the SFU never
 * decodes a frame, which is the whole reason one box holds ~300 participants.
 */
export const EffectsTab = memo(function EffectsTab({ video, runtime, onChange }: EffectsTabProps) {
  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-3 gap-2">
        {KINDS.map((kind) => (
          <Button
            key={kind.id}
            variant={video.kind === kind.id ? 'subtle' : 'outline-neutral'}
            size="md"
            fullWidth
            leftIcon={kind.icon}
            aria-pressed={video.kind === kind.id}
            onClick={() => onChange({ kind: kind.id })}
          >
            {kind.label}
          </Button>
        ))}
      </div>

      {video.kind === 'blur' ? (
        <SettingRow label="Blur strength" hint="Stronger blur hides more of the room behind you.">
          <div className="flex gap-1.5">
            {STRENGTHS.map((strength) => (
              <Button
                key={strength.id}
                variant={video.blurStrength === strength.id ? 'subtle' : 'outline-neutral'}
                size="sm"
                aria-pressed={video.blurStrength === strength.id}
                onClick={() => onChange({ blurStrength: strength.id })}
              >
                {strength.label}
              </Button>
            ))}
          </div>
        </SettingRow>
      ) : null}

      {video.kind === 'image' ? (
        <BackgroundGallery
          selectedId={video.backgroundId}
          onSelect={(backgroundId) => onChange({ backgroundId })}
        />
      ) : null}

      {runtime.reason ? (
        <p
          role="status"
          className={cn('rounded-lg bg-warning-subtle px-3 py-2 text-sm text-secondary-foreground')}
        >
          {runtime.reason}
        </p>
      ) : null}

      {runtime.active ? (
        <p className="text-sm text-muted-foreground">
          Running at {runtime.fps} fps on this device.
        </p>
      ) : null}
    </div>
  );
});
